import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Dialog, DialogContent, DialogTitle, Typography, useColorScheme} from "@mui/material";
import CacheService from "../../../../core/services/cache/cache_service";
import AuthBlockerService from "../../../../core/services/auth-blocker";
import {routes} from "../../../../core/models/static/routes/app-routes";
import useIsMounted from "../../../hooks/use-is-mounted";
import BroadcastService from "../../../../core/services/broadcast";
import {useLocation} from "react-router-dom";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";

export const AuthBlockerDialog: React.FC = () => {
    const [open, setOpen] = useState(AuthBlockerService.isPaused);
    const [urlConfig, setUrlConfig] = useState<any>(null);
    const [mismatchError, setMismatchError] = useState(false);
    const isMounted = useIsMounted();
    const {mode, systemMode} = useColorScheme();
    const [loggingOut, setLoggingOut] = useState<boolean>(false);
    const location = useLocation();
    const isAuthRoute = location.pathname.startsWith(routes.auth.base);
    const [iframeKey, setIframeKey] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    /**@type {'light' | 'dark'}*/
    const iframeColorMode = useMemo(() => {
        switch (mode) {
            case "dark":
            case "light":
                return mode;
            default:
                switch (systemMode) {
                    case "dark":
                    case "light":
                        return systemMode;
                    default:
                        return 'light';
                }
        }
    }, [mode, systemMode])

    const loginUrl = useMemo(() => {
        if (!urlConfig?.logoutUrl) return null;
        const ourUrl = new URL(window.location.origin + routes.auth.login);
        const lockedOperatorId = CacheService.getOperatorId();
        if (lockedOperatorId) {
            ourUrl.searchParams.set('lockedOperatorId', lockedOperatorId.toString());
        }
        return `${urlConfig.logoutUrl}/${encodeURIComponent(ourUrl.toString())}?useQueryForParams=true&colorMode=${iframeColorMode}&`;
    }, [iframeColorMode, urlConfig]);

    /**
     * Attaches the appropriate callbacks to our custom events on the window.
     */
    useEffect(() => {
        const {close} = BroadcastService.addEventListener(BroadcastService.channelNames.authBlockerDialog, 'message', (data) => {
            if (data?.type === 'auth-halt-started') {
                setOpen(true);
                setMismatchError(false);
            } else if (data?.type === 'auth-halt-ended') {
                setOpen(false);
                setMismatchError(false);
            } else if (data?.type === 'auth-mismatch') {
                setMismatchError(true);
            } else if (data?.type === 'auth-mismatch-reset') {
                setMismatchError(false);
            }
        });
        return () => {
            close?.();
        }
    }, []);

    /**
     * Listens for successful login messages posted by the iframe
     */
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'auth-login-success') {
                AuthBlockerService.syncGeneration();
                AuthBlockerService.resume();
                BroadcastService.postMessage(BroadcastService.channelNames.authBlockerService, {type: 'login-success'});
                setMismatchError(false);
            }
            if (event.data?.type === 'auth-login-mismatch') {
                BroadcastService.postMessage(BroadcastService.channelNames.authBlockerDialog, {type: 'auth-mismatch'});
                setMismatchError(true);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    /**
     * Fetches and sets the login URL so we can show the iframe for Core login.
     */
    const fetchAndSetLoginUrl = useCallback(async () => {
        const fetchedUrlConfig = await CacheService.getOrFetchUrlConfig();
        if (!isMounted())
            return;
        if (fetchedUrlConfig?.logoutUrl) {
            setUrlConfig(fetchedUrlConfig);
        }
    }, [isMounted])

    /**
     * Fetches the URL Configs and with it, sets the LoginUrl for our iframe to open up the route for login.
     */
    useEffect(() => {
        if (open) {
            void fetchAndSetLoginUrl();
        }
    }, [open, fetchAndSetLoginUrl]);

    /**
     * Handles the case of operator mismatch where the user logged in with another operator than the one who was originally logged in when the session was paused. In this case, we want to show an error message and give them the option to either enter their credentials again or logout from all tabs and return to Core.
     */
    const enterCredentialsAgain = useCallback(() => {
        BroadcastService.postMessage(BroadcastService.channelNames.authBlockerDialog, {type: 'auth-mismatch-reset'});
        setMismatchError(false);
    }, []);

    /**
     * Logs the user out from all tabs and returns them to Core. This is done by calling the logout function in the CacheService which clears all cached data and then posting a message to the BroadcastService to notify all tabs of the logout so they can also clear their data and redirect to Core if needed.
     */
    const logout = useCallback(async () => {
        setLoggingOut(true);
        await CacheService.logout(true);
        if (!isMounted())
            return;
        setLoggingOut(false);
    }, [isMounted]);

    /**
     * Refreshes the login iframe state to recover from potential internal iframe hangs.
     */
    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        setTimeout(() => {
            setIsRefreshing(false);
            setIframeKey(k => k + 1);
        }, 1000);
    }, []);

    return (
        <Dialog open={open && !isAuthRoute} disableEscapeKeyDown maxWidth={'sm'} fullWidth sx={{zIndex: theme => theme.zIndex.modal + 101}}>
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                Please Login to Continue
                {!mismatchError && loginUrl && (
                    <Button variant="outlined" size="small" onClick={handleRefresh} disabled={isRefreshing}>
                        Refresh
                    </Button>
                )}
            </DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    m: 0,
                    overflow: 'hidden',
                    minHeight: 'min(80vh, 550px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: mismatchError ? 'center' : 'stretch'
                }}
            >
                {
                    mismatchError &&
                    <Box sx={{padding: '2rem', textAlign: 'center', gap: 2, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <Typography variant={'h4'} marginBlockEnd={4}>
                            Operator Mismatch
                        </Typography>
                        <Typography variant={'body2'}>
                            Unfortunately, we only allow the login of the same operator during an active session pause. You have logged in with another user.
                        </Typography>
                        <Box sx={{marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center'}}>
                            <Button
                                variant={'contained'}
                                size={'medium'}
                                onClick={enterCredentialsAgain}
                            >
                                Enter your credentials again
                            </Button>
                            <Button
                                size={'medium'}
                                variant={'text'}
                                onClick={logout}
                                disabled={loggingOut}
                            >
                                {loggingOut ? 'Logging out...' : "Logout from all tabs and go to Core"}
                            </Button>
                        </Box>
                    </Box>
                }
                {
                    !mismatchError &&
                    loginUrl &&
                    (isRefreshing ? (
                        <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <LoadingLottieAnimation width={'300px'}/>
                        </Box>
                    ) : (
                        <iframe
                            key={iframeKey}
                            src={loginUrl}
                            style={{width: '100%', height: '100%', border: 'none', overflow: 'auto', flex: 1}}
                            title="Login"
                            allow="local-network-access *"
                        />
                    ))
                }
            </DialogContent>
        </Dialog>
    );
};
